import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import FileDropZone from "@/components/FileDropZone";
import ProjectStats from "@/components/ProjectStats";
import {
  FolderOpen, Trash2, ChevronRight, Plus, Sparkles, Clock,
  Globe, Languages
} from "lucide-react";

export default function Dashboard({ projects, onProjectSelect, onProjectCreated, onDeleteProject, onRefresh }) {
  const [isUploading, setIsUploading] = useState(false);
  const [creatingDemo, setCreatingDemo] = useState(false);

  // Auto-refresh when any project is translating
  useEffect(() => {
    const hasTranslating = projects.some(p => p.status === 'translating');
    if (!hasTranslating) return;
    const interval = setInterval(() => { onRefresh(); }, 5000);
    return () => clearInterval(interval);
  }, [projects, onRefresh]);
  const navigate = useNavigate();

  const handleFilesSelected = useCallback(async (files) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));

      const res = await axios.post(`${API}/projects/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onProjectCreated(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to import files";
      toast.error(msg);
    } finally {
      setIsUploading(false);
    }
  }, [onProjectCreated]);

  const handleCreateDemo = useCallback(async () => {
    setCreatingDemo(true);
    try {
      const res = await axios.post(`${API}/projects/demo`);
      onProjectCreated(res.data);
      toast.success("Demo project created with 20 game segments");
    } catch (e) {
      toast.error("Failed to create demo project");
    } finally {
      setCreatingDemo(false);
    }
  }, [onProjectCreated]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight uppercase text-[#E0E0E0] mb-2">
          Mission Control
        </h1>
        <p className="text-base font-ui text-[#94A3B8]">
          fanzhang's Workspace &middot; SDL Trados Game Localization &middot; ZH-CN → DE-DE
        </p>
      </div>

      {/* Import section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-heading font-semibold uppercase tracking-wider text-[#94A3B8]">
            Import Project
          </h2>
          <button
            onClick={handleCreateDemo}
            disabled={creatingDemo}
            className="flex items-center gap-2 px-4 py-2 rounded-sm bg-neon-purple/10 hover:bg-neon-purple/20 text-[#7000FF] border border-[#7000FF]/30 hover:border-[#7000FF]/60 font-ui text-sm font-medium uppercase tracking-wider transition-colors duration-200 disabled:opacity-50"
            data-testid="create-demo-btn"
          >
            {creatingDemo ? (
              <div className="w-4 h-4 border-2 border-[#7000FF] border-t-transparent rounded-full animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            )}
            Load Demo Project
          </button>
        </div>
        <FileDropZone onFilesSelected={handleFilesSelected} isUploading={isUploading} />
      </div>

      {/* Projects list */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-heading font-semibold uppercase tracking-wider text-[#94A3B8]">
            Projects ({projects.length})
          </h2>
        </div>

        {projects.length === 0 ? (
          <div className="glass-card p-12 text-center" data-testid="no-projects">
            <FolderOpen className="w-12 h-12 text-[#2A2F3A] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#64748B] font-ui text-sm">
              No projects yet. Import Trados files or load a demo project to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {projects.map((project, idx) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={idx}
                onSelect={() => onProjectSelect(project)}
                onDelete={() => onDeleteProject(project.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectCard({ project, index, onSelect, onDelete }) {
  const total = project.total_segments || 1;
  const translated = (project.tm_100_count || 0) + (project.tm_fuzzy_count || 0) + (project.ai_translated_count || 0);
  const progress = Math.round((translated / total) * 100);

  const statusColor = {
    importing: '#FFB800',
    ready: '#94A3B8',
    translating: '#00F0FF',
    translated: '#39FF14',
    exported: '#7000FF',
  }[project.status] || '#94A3B8';

  return (
    <div
      className="glass-card group hover:border-[#4A5568] transition-colors duration-200 stagger-item cursor-pointer"
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`project-card-${project.id}`}
    >
      <div className="flex items-center gap-4 p-4" onClick={onSelect}>
        {/* Status dot */}
        <div className="status-dot flex-shrink-0" style={{ color: statusColor, backgroundColor: statusColor }} />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-heading font-semibold text-[#E0E0E0] truncate">{project.name}</h3>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm uppercase tracking-wider border"
              style={{
                color: statusColor,
                borderColor: `${statusColor}40`,
                background: `${statusColor}10`,
              }}
            >
              {project.status}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-[#64748B] font-ui">
            <span className="flex items-center gap-1">
              <Languages className="w-3 h-3" strokeWidth={1.5} />
              {project.source_language} → {project.target_language}
            </span>
            <span className="flex items-center gap-1">
              <Globe className="w-3 h-3" strokeWidth={1.5} />
              {project.total_segments} segments
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" strokeWidth={1.5} />
              {new Date(project.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-[#64748B] font-ui mb-0.5">Progress</p>
            <div className="w-24 h-1 bg-surface-300 rounded-full overflow-hidden">
              <div className="h-full bg-neon-cyan rounded-full" style={{ width: `${progress}%`, boxShadow: '0 0 6px rgba(0, 240, 255, 0.4)' }} />
            </div>
            <p className="text-[10px] font-mono text-neon-cyan mt-0.5">{progress}%</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-2 rounded-sm hover:bg-neon-error/10 text-[#64748B] hover:text-neon-error transition-colors duration-200"
            data-testid={`delete-project-${project.id}`}
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <ChevronRight className="w-5 h-5 text-[#4A5568] group-hover:text-neon-cyan transition-colors duration-200" strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
}
